import { Locales } from '@griply/types';
import { LocalizedText } from '@griply/utils';
import imagePage1 from 'shared/assets/images/whats-new/3.52.5-new-languages.jpg';
import imagePage1x2 from 'shared/assets/images/whats-new/3.52.5-new-languages@2x.jpg';
import imagePage1x3 from 'shared/assets/images/whats-new/3.52.5-new-languages@3x.jpg';
// Deliberate mobile parallel — `image`/`imageSrcSet` are platform-divergent and the content is not shared.
export type WhatsNewBullet = {
  title: LocalizedText;
  description?: LocalizedText;
};

export type WhatsNewPage = {
  image?: string;
  imageSrcSet?: string;
  newBadge?: boolean;
  title: LocalizedText;
  subtitle?: LocalizedText;
  bullets: WhatsNewBullet[];
  link?: { url: string; title: LocalizedText };
};

export type WhatsNewContent = {
  version: string;
  pages: WhatsNewPage[];
};

// The dialog is keyed to `version`, NOT the app's build version. Each user is
// shown a given `version` exactly once (tracked in the profile). Rules:
//
// - New notes to show      → bump `version` AND replace the pages.
// - Same notes, new build   → leave `version` (hot fix, patch, phased reship).
//   This is how patch releases (3.xx.1 / 3.xx.2) are skipped: leave `version`.
// - No dialog at all        → set the whole value to `null`.
//
// Bumping `version` re-shows the dialog to everyone who already saw the old one;
// leaving it still reaches anyone who skipped the prior build.
export const WHATS_NEW_CONTENT: WhatsNewContent | null = {
  version: '3.52.5',
  pages: [
    {
      image: imagePage1,
      imageSrcSet: `${imagePage1} 1x, ${imagePage1x2} 2x, ${imagePage1x3} 3x`,
      newBadge: true,
      title: {
        [Locales.EnUS]: 'Griply in 8 more languages',
        [Locales.NlNL]: 'Griply in 8 extra talen',
        [Locales.DeDE]: 'Griply in 8 weiteren Sprachen',
        [Locales.EsES]: 'Griply en 8 idiomas más',
      },
      subtitle: {
        [Locales.EnUS]: 'Plan your day in the language you think in.',
        [Locales.NlNL]: 'Plan je dag in de taal waarin je denkt.',
        [Locales.DeDE]: 'Plan deinen Tag in der Sprache, in der du denkst.',
        [Locales.EsES]: 'Planifica tu día en el idioma en el que piensas.',
      },
      bullets: [
        {
          title: {
            [Locales.EnUS]: 'New languages',
            [Locales.NlNL]: 'Nieuwe talen',
            [Locales.DeDE]: 'Neue Sprachen',
            [Locales.EsES]: 'Idiomas nuevos',
          },
          description: {
            [Locales.EnUS]:
              'Use Griply in Polish, Danish, Swedish, Norwegian, Italian, French or Portuguese (Portugal and Brazil).',
            [Locales.NlNL]:
              'Gebruik Griply in het Pools, Deens, Zweeds, Noors, Italiaans, Frans of Portugees (Portugal en Brazilië).',
            [Locales.DeDE]:
              'Griply auf Polnisch, Dänisch, Schwedisch, Norwegisch, Italienisch, Französisch oder Portugiesisch (Portugal und Brasilien).',
            [Locales.EsES]:
              'Usa Griply en polaco, danés, sueco, noruego, italiano, francés o portugués (Portugal y Brasil).',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Smart task recognition',
            [Locales.NlNL]: 'Slimme taakherkenning',
            [Locales.DeDE]: 'Smarte Aufgabenerkennung',
            [Locales.EsES]: 'Reconocimiento inteligente',
          },
          description: {
            [Locales.EnUS]:
              "Type a task the way you'd say it and Griply picks up the date, time and repeat in every new language.",
            [Locales.NlNL]:
              'Typ een taak zoals je hem zou zeggen en Griply herkent datum, tijd en herhaling in elke nieuwe taal.',
            [Locales.DeDE]:
              'Tipp eine Aufgabe so, wie du sie sagen würdest, und Griply erkennt Datum, Uhrzeit und Wiederholung in jeder neuen Sprache.',
            [Locales.EsES]:
              'Escribe una tarea tal como la dirías y Griply reconoce la fecha, la hora y la repetición en todos los idiomas nuevos.',
          },
        },
        {
          title: {
            [Locales.EnUS]: 'Switch anytime',
            [Locales.NlNL]: 'Wissel wanneer je wilt',
            [Locales.DeDE]: 'Jederzeit wechseln',
            [Locales.EsES]: 'Cambia cuando quieras',
          },
          description: {
            [Locales.EnUS]:
              'Griply follows your system language. Prefer another? Pick one under Settings > Language.',
            [Locales.NlNL]:
              'Griply volgt de taal van je systeem. Liever een andere? Kies er een bij Instellingen > Taal.',
            [Locales.DeDE]:
              'Griply folgt deiner Systemsprache. Lieber eine andere? Wähl sie unter Einstellungen > Sprache.',
            [Locales.EsES]:
              'Griply sigue el idioma de tu sistema. ¿Prefieres otro? Elígelo en Ajustes > Idioma.',
          },
        },
      ],
    },
  ],
};
